export const CLIENT_THEME_STORAGE_KEY = "clientTheme";
export const DEFAULT_PRIMARY_COLOR = "#422C75";

export const DEFAULT_CLIENT_THEME = {
    primaryColor: DEFAULT_PRIMARY_COLOR,
    primaryColorDark: "#362460",
    logoUrl: "",
};

export const isValidHexColor = (color) => /^#[0-9A-Fa-f]{6}$/.test(color || "");

const clamp = (value) => Math.max(0, Math.min(255, value));

export const darkenHexColor = (hexColor, factor = 0.18) => {
    if (!isValidHexColor(hexColor)) {
        return DEFAULT_CLIENT_THEME.primaryColorDark;
    }

    const normalizedFactor = Math.max(0, Math.min(1, factor));
    const red = clamp(Math.round(parseInt(hexColor.slice(1, 3), 16) * (1 - normalizedFactor)));
    const green = clamp(Math.round(parseInt(hexColor.slice(3, 5), 16) * (1 - normalizedFactor)));
    const blue = clamp(Math.round(parseInt(hexColor.slice(5, 7), 16) * (1 - normalizedFactor)));

    return `#${[red, green, blue].map((value) => value.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
};

const detectBase64MimeType = (base64) => {
    if (!base64) {
        return "";
    }

    if (base64.startsWith("iVBOR")) {
        return "image/png";
    }

    if (base64.startsWith("/9j/")) {
        return "image/jpeg";
    }

    if (base64.startsWith("UklGR")) {
        return "image/webp";
    }

    return "";
};

export const normalizeLogoSource = (logo) => {
    if (!logo || typeof logo !== "string") {
        return "";
    }

    const trimmedLogo = logo.trim();

    if (!trimmedLogo) {
        return "";
    }

    // Se já for um Data URL, retorna como está.
    if (trimmedLogo.startsWith("data:")) {
        return trimmedLogo;
    }

    // Detecta Base64 de imagem.
    const mimeType = detectBase64MimeType(trimmedLogo);

    if (mimeType) {
        return `data:${mimeType};base64,${trimmedLogo}`;
    }

    return "";
};

export const buildClientTheme = (user = {}) => {
    const primaryColor = isValidHexColor(user.clientPrimaryColor)
        ? user.clientPrimaryColor.toUpperCase()
        : DEFAULT_PRIMARY_COLOR;

    return {
        primaryColor,
        primaryColorDark: darkenHexColor(primaryColor),
        logoUrl: normalizeLogoSource(user.clientLogo),
    };
};

export const getStoredClientTheme = () => {
    try {
        const rawTheme = sessionStorage.getItem(CLIENT_THEME_STORAGE_KEY);
        if (!rawTheme) {
            return { ...DEFAULT_CLIENT_THEME };
        }

        const parsedTheme = JSON.parse(rawTheme);
        const primaryColor = isValidHexColor(parsedTheme?.primaryColor)
            ? parsedTheme.primaryColor.toUpperCase()
            : DEFAULT_CLIENT_THEME.primaryColor;

        return {
            primaryColor,
            primaryColorDark: isValidHexColor(parsedTheme?.primaryColorDark)
                ? parsedTheme.primaryColorDark.toUpperCase()
                : darkenHexColor(primaryColor),
            logoUrl: typeof parsedTheme?.logoUrl === "string" ? parsedTheme.logoUrl : "",
        };
    } catch (error) {
        return { ...DEFAULT_CLIENT_THEME };
    }
};

export const storeClientTheme = (theme) => {
    sessionStorage.setItem(CLIENT_THEME_STORAGE_KEY, JSON.stringify(theme));
};

export const clearStoredClientTheme = () => {
    sessionStorage.removeItem(CLIENT_THEME_STORAGE_KEY);
};